import curry from 'lodash/curry';
import Immutable from 'immutable';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { expandField } from 'app/actions/SearchActions';
import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import { FILTER_PREFIX, FILTER_SEPARATOR } from 'app/constants/BibConstants';
import {
  BROWSE_CATEGORY_PAGE_FACET_FIELD_IDS,
  FIELD_ID,
  GROUP_ID,
  FILTERABLE_DISPLAY_BIB_GRID_LIMIT,
  PAGE_KEY
} from 'app/constants/BrowseConstants';
import { mapSearchBibToDisplayBib } from 'app/helpers/displayBib';
import {
  makeFieldLabel,
  makeFilterLabel,
  makeGroupLabel,
  mapCatalogSearchFieldsToNewFacets,
  mapCatalogSearchParamsToFacetFieldsSearchParams
} from 'app/helpers/browse';
import { selectBibEntities } from 'app/selectors/EntitiesSelector';
import FilterableDisplayBibGrid from '../shared/FilterableDisplayBibGrid';
import BrowseSearchPage from './BrowseSearchPage';

const HEADING_QUERY_STRING_KEY = 'title';

const BrowseSearchPageContainer = () => {
  const dispatch = useDispatch();
  const { push } = useHistory();
  const intl = useIntl();
  const { search } = useLocation();
  const catalogSearch = useSelector(state => state.getIn(['search', 'catalogSearch']));
  const searchBibs = useSelector(selectBibEntities);

  const displayBibs = catalogSearch
    .get('results', Immutable.List())
    .map(result => {
      const metadataId = result.get('representative');

      return searchBibs.get(metadataId, Immutable.Map()).toJS();
    })
    .map(mapSearchBibToDisplayBib)
    .toArray();

  const appliedFiltersAccessibilityHeading = 'TODO: Accessibility Heading';
  const facetsHeading = intl.formatMessage({
    defaultMessage: 'Browse facets',
    id: 'browse_facets'
  });
  const isFetching = catalogSearch.get('isFetching');
  const pagination = catalogSearch.get('pagination');

  const handleFilterChange = (filter, newIsApplied) => {
    const { fieldId, filterId } = filter;
    const newSearchParams = new URLSearchParams(search);
    const catalogSearchKey = `${FILTER_PREFIX}${fieldId}`;
    const currentValues = (newSearchParams.get(catalogSearchKey) || '').split(FILTER_SEPARATOR).filter(Boolean);
    let newValues;

    if (newIsApplied) {
      newValues = currentValues.concat(filterId);
    } else {
      newValues = currentValues.filter(value => value !== filterId).filter(Boolean);
    }

    if (newValues.length) {
      newSearchParams.set(catalogSearchKey, newValues.join(FILTER_SEPARATOR));
    } else {
      newSearchParams.delete(catalogSearchKey);
    }

    push(`?${newSearchParams.toString()}`);
  };

  const setShowMoreFieldId = fieldId => {
    const newSearchParams = new URLSearchParams(search);
    const searchQueryParams = {
      ...newSearchParams,
      limit: FILTERABLE_DISPLAY_BIB_GRID_LIMIT
    };

    dispatch(expandField(fieldId, searchQueryParams));
  };

  const unapplyAllFilters = () => {
    const newSearchParams = new URLSearchParams(search);

    Array.from(newSearchParams.keys()).forEach(key => {
      if (key.startsWith(FILTER_PREFIX)) {
        newSearchParams.delete(key);
      }
    });

    newSearchParams.delete(PAGE_KEY);

    push(`?${newSearchParams.toString()}`);
  };

  const fields = catalogSearch.get('fields');
  const formatFieldLabel = curry(makeFieldLabel)(intl);
  const searchParams = mapCatalogSearchParamsToFacetFieldsSearchParams(new URLSearchParams(search));
  const showMoreFieldId = catalogSearch.get('showMoreFiltersFieldId');
  const facetFields = mapCatalogSearchFieldsToNewFacets(fields.toJS(), {
    formatFieldLabel,
    formatFilterLabel: curry(makeFilterLabel)(intl),
    formatGroupLabel: curry(makeGroupLabel)(intl),
    formatShowMoreOverlayTitle: fieldId => {
      const fieldLabel = formatFieldLabel({ fieldId, isPlural: true });
      return intl.formatMessage({ defaultMessage: 'All {fieldLabel}', id: 'all_field_filters' }, { fieldLabel });
    },
    orderedFieldIds: BROWSE_CATEGORY_PAGE_FACET_FIELD_IDS,
    orderedGroupIdsByFieldId: {
      [FIELD_ID.FORMAT]: [
        GROUP_ID.BOOKS,
        GROUP_ID.AUDIOBOOKS_SPOKEN_WORD,
        GROUP_ID.MOVIES_TV_VIDEO,
        GROUP_ID.MUSIC_SOUND,
        GROUP_ID.GAMES_INTERACTIVE_MEDIA,
        GROUP_ID.JOURNALS_MAGAZINES,
        GROUP_ID.ACCESSIBLE_FORMATS,
        GROUP_ID.OTHER
      ]
    },
    searchParams,
    showMoreFieldId
  });

  const backToPageTitle = intl.formatMessage({ id: 'browse' });
  const backToLinkProps = {
    href: '/v2/browse',
    label: backToPageTitle,
    screenReaderLabel: intl.formatMessage(
      {
        defaultMessage: 'Back to {pageTitle}',
        id: 'sr_back_to_page_title'
      },
      {
        pageTitle: backToPageTitle
      }
    )
  };

  const urlSearchParams = new URLSearchParams(search);
  const heading = urlSearchParams.get(HEADING_QUERY_STRING_KEY) || intl.formatMessage({ id: 'browse' });

  return (
    <>
      <DocumentHeadContainer pageTitle={heading} />
      <BrowseSearchPage backToLinkProps={backToLinkProps} heading={heading}>
        <FilterableDisplayBibGrid
          appliedFiltersAccessibilityHeading={appliedFiltersAccessibilityHeading}
          displayBibs={displayBibs}
          facetFields={facetFields}
          facetsHeading={facetsHeading}
          handleFilterChange={handleFilterChange}
          hasBibRatings
          isFetching={isFetching}
          pagination={pagination}
          setShowMoreFieldId={setShowMoreFieldId}
          unapplyAllFilters={unapplyAllFilters}
        />
      </BrowseSearchPage>
    </>
  );
};

export default BrowseSearchPageContainer;
